import { useForm } from "react-hook-form";
import { Forms } from "@/types/form";
import { Styled } from "../styles";
import { Input } from "@dippi/ui";
import { Box, Button, Checkbox, Grid, Link, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRegister } from "./hooks";
import schemas from "@/utils/schemas";
import { useUser } from "@/context/user";
import { useState } from "react";
import constant from "@/utils/constant";
import GoogleButton from "@/components/common/google-button";
import useCountdown from "@/hooks/auth/useCountdown";
import useGoogleSign from "@/hooks/auth/useGoogleSign";
import useCheckEmailStatus from "@/hooks/auth/useCheckEmailStatus";

const SignupForm: React.FC = () => {
    const { control, handleSubmit, getValues } = useForm<Forms["Register"]>({
        resolver: yupResolver(schemas.register),
        defaultValues: { email: "", password: "", confirmPassword: "" },
    });

    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const onCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAgreedToTerms(event.target.checked);
    };

    const { countdown, counting, startCountdown } = useCountdown();
    const { onRegister, registered, onResendLink } = useRegister(startCountdown);
    const onGoogleSign = useGoogleSign();

    useCheckEmailStatus({ check: registered });

    const loading = useUser(state => state.globalLoading);

    if (registered) {
        const email = getValues("email");
        return (
            <Styled.Form>
                <Grid container rowGap={constant.GRID_SPACING}>
                    <Grid item xs={12}>
                        <Typography textAlign="center" variant="h3" marginBottom={0}>
                            Let's verify your email
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography textAlign="center">
                            Hey, please check <b>{email}</b> to verify your account and get started.
                        </Typography>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography>
                            {counting
                                ? `Resend mail in ${countdown} sec.`
                                : "You can resend mail now."}
                        </Typography>
                        <Button
                            data-testid="signup-resend-button"
                            variant="outlined"
                            type="button"
                            disabled={counting}
                            onClick={onResendLink.bind(this, email)}
                        >
                            Resend email
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>
                            Need help?
                            <Link
                                data-testid="signup-contact-us-link"
                                href="https://dippi.app/company"
                                underline="none"
                                target="_blank"
                                rel="noreferrer noopener"
                                marginLeft="10px"
                            >
                                Contact us
                            </Link>
                        </Typography>
                    </Grid>
                </Grid>
            </Styled.Form>
        );
    }

    return (
        <Styled.Form onSubmit={handleSubmit(async form => await onRegister(form))}>
            <Grid container rowGap={constant.GRID_SPACING}>
                <Grid item xs={12}>
                    <Typography textAlign="center" variant="h3" marginBottom={0}>
                        Create your Dippi account
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <GoogleButton
                        data-testid="signup-google-button"
                        text="Sign up with Google"
                        onClick={onGoogleSign}
                        style={{ width: "100%" }}
                        tabIndex={1}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography marginBottom="10px" variant="body2">
                        Or signup with email:
                    </Typography>
                    <Input
                        testId="signup-email-input"
                        name="email"
                        control={control}
                        label="Email"
                        type="email"
                        tabIndex={2}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Input
                        testId="signup-password-input"
                        name="password"
                        control={control}
                        label="Password"
                        type="password"
                        className="sentry-mask"
                        tabIndex={3}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Input
                        testId="signup-confirm-password-input"
                        name="confirmPassword"
                        control={control}
                        label="Confirm password"
                        type="password"
                        className="sentry-mask"
                        tabIndex={4}
                        autoComplete="off"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        marginBottom="5px"
                    >
                        <Checkbox
                            data-testid="signup-policy-checkbox"
                            checked={agreedToTerms}
                            onChange={onCheck}
                            style={{ padding: "6px 9px 6px 0px" }}
                            disableRipple
                        />
                        <Typography textAlign="center" variant="body2">
                            By continuing, you agree to Dippi's{" "}
                            <Link
                                data-testid="signup-policy-link"
                                href="https://dippi.app/privacy-policy"
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                Privacy Policy
                            </Link>
                        </Typography>
                    </Box>
                    <Button
                        data-testid="signup-button"
                        tabIndex={5}
                        type="submit"
                        fullWidth={true}
                        disabled={loading || !agreedToTerms}
                    >
                        Sign up
                    </Button>
                </Grid>
            </Grid>
        </Styled.Form>
    );
};

export default SignupForm;
